import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type PaymentQrDetails } from '../api/client';
import Button from './ui/Button';
import './PaymentDetailsBlock.css';

type CopyableField = 'account' | 'vs';

interface PaymentDetailsBlockProps {
  /** `event-generic` is the admin event QR, which carries no variable symbol. */
  type: 'event' | 'membership' | 'event-generic';
  entityId: number | string;
  /** Required for `event` and `membership`; ignored for `event-generic`. */
  memberId?: number | string;
  /** Admins hit the gated admin routes; everyone else the policy-gated /my ones. */
  isAdmin: boolean;
}

/**
 * Account number, variable symbol and amount printed under a payment QR.
 *
 * The image alone is useless to a payer whose bank app fails to read it, and a
 * variable symbol is ten digits to retype on a phone — hence the copy buttons.
 * The figures come from the `*-qr-details` sibling of whichever QR endpoint
 * produced the image, so the two cannot disagree.
 *
 * The amount is what is still owed, the same figure the QR encodes and the same
 * one the surrounding UI shows — see `buildQrDetails`, which subtracts
 * `paidAmount`.
 *
 * Props are primitives rather than one subject object on purpose: an object
 * literal from the parent would be a new identity on every render and refetch
 * continuously.
 *
 * Renders nothing when the details fail to load — the QR component above
 * already surfaces that error, and repeating it adds noise.
 */
const PaymentDetailsBlock: React.FC<PaymentDetailsBlockProps> = ({
  type,
  entityId,
  memberId,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const [details, setDetails] = useState<PaymentQrDetails | null>(null);
  const [copied, setCopied] = useState<CopyableField | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const entity = String(entityId);
    const member = memberId != null ? String(memberId) : null;

    const fetchDetails = async () => {
      try {
        let response;
        if (type === 'event-generic') {
          response = await api.payments.eventQrDetails(entity, controller.signal);
        } else if (member == null) {
          return;
        } else if (type === 'event') {
          response = isAdmin
            ? await api.payments.getQRDetails(entity, member, controller.signal)
            : await api.my.payments.qrDetails(entity, member, controller.signal);
        } else {
          response = isAdmin
            ? await api.payments.getMembershipQRDetails(entity, member, controller.signal)
            : await api.my.payments.membershipQrDetails(entity, member, controller.signal);
        }
        setDetails(response.data);
      } catch {
        if (!controller.signal.aborted) setDetails(null);
      }
    };

    fetchDetails();
    return () => controller.abort();
  }, [type, entityId, memberId, isAdmin]);

  // Clear the transient "copied" flag; the cleanup also covers unmount.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = useCallback(async (value: string, field: CopyableField) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(field);
    } catch {
      // Denied or unavailable; the value stays on screen to read.
    }
  }, []);

  if (!details) return null;

  // Absent outside a secure context (plain http), where there is no reliable
  // fallback — the figures stay selectable instead.
  const canCopy = typeof navigator.clipboard?.writeText === 'function';

  const rows: Array<{
    key: CopyableField | 'amount';
    label: string;
    value: string;
    copyField: CopyableField | null;
  }> = [
    {
      key: 'account',
      label: t('payments.accountNumber'),
      value: details.accountNumber,
      copyField: 'account',
    },
    ...(details.variableSymbol
      ? [{
        key: 'vs' as const,
        label: t('payments.variableSymbol'),
        value: details.variableSymbol,
        copyField: 'vs' as const,
      }]
      : []),
    {
      key: 'amount',
      label: t('payments.amount'),
      value: `${details.amount.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })} CZK`,
      copyField: null,
    },
  ];

  return (
    <dl className="payment-details">
      {rows.map((row) => (
        <div className="payment-details__row" key={row.key}>
          <dt className="payment-details__label">{row.label}</dt>
          <dd className="payment-details__value">
            <span className="payment-details__figure">{row.value}</span>
            {row.copyField && canCopy && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="payment-details__copy"
                onClick={() => copy(row.value, row.copyField!)}
                aria-label={`${t('payments.copy')} ${row.label}`}
              >
                {copied === row.copyField ? t('payments.copied') : t('payments.copy')}
              </Button>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export default PaymentDetailsBlock;
